import { useState } from 'react';
import { App, Button, DatePicker, Popover } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { getDealDetailQueryKey, updateDeal, type DealDetail } from '../api/deals.client.js';

export function DealNextContact({ dealId, dueAt }: { dealId: string; dueAt: string | null }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const scope = useCurrentScope();
  const cache = useQueryClient();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const detailKey = getDealDetailQueryKey(workspaceId, dealId);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<Dayjs | null>(null);
  const mutation = useMutation({
    mutationFn: (nextActivityDue: string | null) => updateDeal(dealId, { nextActivityDue }),
    onMutate: async (nextActivityDue) => {
      await cache.cancelQueries({ queryKey: detailKey });
      const previous = cache.getQueryData<DealDetail>(detailKey);
      if (previous) cache.setQueryData(detailKey, { ...previous, nextActivityDue });
      return { previous };
    },
    onError: (_error, _dueAt, context) => {
      if (context?.previous) cache.setQueryData(detailKey, context.previous);
      void message.error(t('crm.entity_detail.save_error'));
    },
    onSuccess: () => {
      setOpen(false);
      void message.success(t('crm.deal_detail.next_contact_saved'));
    },
    onSettled: async () => {
      await Promise.all(
        ['deals', 'hot-leads', 'activities'].map((root) =>
          cache.invalidateQueries({ queryKey: [root, workspaceId] }),
        ),
      );
    },
  });
  const relativePreset = (days: number): Dayjs => {
    const now = dayjs().add(days, 'day');
    return now
      .minute(Math.ceil(now.minute() / 15) * 15)
      .second(0)
      .millisecond(0);
  };
  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        if (next) setValue(dueAt ? dayjs(dueAt) : null);
        setOpen(next);
      }}
      title={t('crm.deal_detail.next_step')}
      content={
        <div className="deal-next-contact-editor">
          <DatePicker
            // antd 6: popupClassName → classNames.popup.root.
            classNames={{ popup: { root: 'deal-next-contact-picker' } }}
            aria-label={t('crm.deal_detail.next_step')}
            value={value}
            onChange={(next) => setValue(next?.second(0).millisecond(0) ?? null)}
            showTime={{ format: 'HH:mm', minuteStep: 15 }}
            format="DD.MM.YYYY HH:mm"
            needConfirm={false}
            disabled={mutation.isPending}
            presets={[
              {
                label: t('crm.deal_detail.preset_today'),
                value: () => dayjs().hour(18).minute(0).second(0).millisecond(0),
              },
              {
                label: t('crm.deal_detail.preset_tomorrow'),
                value: () => dayjs().add(1, 'day').hour(10).minute(0).second(0).millisecond(0),
              },
              { label: t('crm.deal_detail.preset_three_days'), value: () => relativePreset(3) },
              { label: t('crm.deal_detail.preset_week'), value: () => relativePreset(7) },
            ]}
          />
          <div className="entity-inline-actions">
            <Button
              size="small"
              disabled={!value?.isValid() || mutation.isPending}
              loading={mutation.isPending}
              onClick={() => {
                if (value?.isValid()) mutation.mutate(value.toISOString());
              }}
            >
              {t('crm.entity_detail.save')}
            </Button>
            <Button
              size="small"
              disabled={!dueAt || mutation.isPending}
              onClick={() => mutation.mutate(null)}
            >
              {t('crm.deal_detail.clear')}
            </Button>
          </div>
        </div>
      }
    >
      <Button size="small" disabled={scope === null}>
        {t(dueAt ? 'crm.deal_detail.change_contact' : 'crm.deal_detail.assign_contact')}
      </Button>
    </Popover>
  );
}
